import type { Transaction } from "@rocicorp/zero";
import * as z from "zod";
import { type Context, userFacingError } from "../authorization";
import { type Schema, zql } from "../generatedDrizzleSchema";
import type { FnArgs } from "./resolve";
import { certificateAuditMetadata } from "../connectors/caCertificate";
import {
  MANAGED_AI_CONNECTOR_TYPE,
  managedAiOffForTenant,
} from "../connectors/managedAi";
import { isFeatureFlagEnabled } from "../featureFlags";
import { MANAGED_CONNECTORS_FLAG } from "../featureFlagNames";
import { normalizedConnectorIdentity } from "../connectors/normalizeUrlPattern";
import { hasAccess } from "../rbac/access";
import { grantCustomRoleResolve } from "../rbac/grantResources";
import {
  assertRoleDoesNotGrant,
  grantEntry,
  insertGrantsDeduped,
  validateGrants,
} from "../rbac/validation";
import { creatorColumns, creatorGrantPrincipal } from "../rbac/roles";
import {
  createOAuthConnectionSchema,
  insertFreshOAuthConnection,
} from "./freshOAuthConnection";
import { mutator } from "./mutator";
import { generateId } from "../id";
import { stringRecord } from "./utils";
import { CONNECTOR_NOTE_MAX_LENGTH } from "./updateConnectedApp";
import { clientGeneratableIdSchema } from "../id";

export const createConnectorSchema = z.object({
  connectedAppId: clientGeneratableIdSchema,
  name: z.string(),
  identifier: z.string(),
  urlPattern: z.string(),
  configuration: stringRecord,
  secrets: stringRecord,
  oauthConnectionId: z.string().optional(),
  createOAuthConnection: createOAuthConnectionSchema.optional(),
  grants: z.array(grantEntry).optional(),
  note: z.string().max(CONNECTOR_NOTE_MAX_LENGTH).optional(),
});

export const createConnectedApp = mutator({
  redactKeys: ["secrets"],
  schema: createConnectorSchema,
  resolve: grantCustomRoleResolve,
  permissions: [
    {
      permission: "tenant:connected_app_create",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
  ],
  fn: async (opts) => {
    await createConnector(opts, async (row) => {
      await opts.tx.mutate.connectedApps.insert(row);
      if (!(await opts.tx.run(zql.connectedApps.where("id", row.id).one())))
        throw userFacingError("Connector ID is already in use");
    });
  },
});

async function assertManagedAiConnectorAvailable(
  tx: Transaction<Schema>,
  tenantId: string
) {
  if (!(await isFeatureFlagEnabled(tx, MANAGED_CONNECTORS_FLAG, tenantId))) {
    throw userFacingError("3B managed AI isn’t available yet");
  }
  const tenant = await tx.run(zql.tenants.where("id", tenantId).one());
  if (managedAiOffForTenant(tenant)) {
    throw userFacingError("Managed AI is turned off for this tenant");
  }
}

type ConnectorArgs = FnArgs<
  z.infer<typeof createConnectorSchema>,
  typeof grantCustomRoleResolve
>;

function connectorRow(ctx: Context, args: ConnectorArgs) {
  return {
    id: args.connectedAppId,
    ...normalizedConnectorIdentity(args),
    metadata: {},
    oauthConnectionId: args.createOAuthConnection ? generateId() : null,
    oauthClientId: args.createOAuthConnection?.clientId ?? "",
    hasOAuthClientSecret: false,
    oauthRequestedScopes: args.createOAuthConnection?.scopes ?? [],
    authorizationMode: "owner",
    personalUse: "enabled",
    createdAt: Date.now(),
    ...creatorColumns(ctx),
    fresh: true,
    note: args.note ?? null,
    tenantId: ctx.tenantId,
  } as const;
}

export async function createConnector(
  {
    tx,
    ctx,
    args,
  }: { tx: Transaction<Schema>; ctx: Context; args: ConnectorArgs },
  insertConnector: (row: ReturnType<typeof connectorRow>) => Promise<void>
) {
  const { grants, grantCustomRoles, createOAuthConnection } = args;
  if (args.oauthConnectionId !== undefined)
    throw userFacingError(
      "Connectors can’t reuse an existing OAuth credential"
    );

  // Zero inserts ignore duplicate IDs, so serialize creation before writing secrets or granting access.
  if (tx.location === "server")
    await tx.dbTransaction.query("SELECT pg_advisory_xact_lock(hashtext($1))", [
      `connector-create:${args.connectedAppId}`,
    ]);
  if (await tx.run(zql.connectedApps.where("id", args.connectedAppId).one()))
    throw userFacingError("Connector ID is already in use");

  const resolvedGrants = grants
    ? validateGrants(grants, "connected_app", grantCustomRoles)
    : [];
  const row = connectorRow(ctx, args);
  if (row.identifier === MANAGED_AI_CONNECTOR_TYPE.identifier) {
    await assertManagedAiConnectorAvailable(tx, ctx.tenantId);
  }

  if (createOAuthConnection && row.oauthConnectionId) {
    await insertFreshOAuthConnection(
      tx,
      createOAuthConnection,
      row.oauthConnectionId,
      ctx.tenantId
    );
  }

  await insertConnector(row);

  if (grants) {
    const canShare = await hasAccess({
      tx,
      userId: ctx.userId,
      principal: ctx.principal,
      scopeId: ctx.tenantId,
      tenantId: ctx.tenantId,
      permission: "tenant:connected_app_share",
    });
    if (!canShare) {
      const creator = creatorGrantPrincipal(ctx);
      for (const { input: grant, role } of resolvedGrants) {
        const toCreator =
          "serviceAccountId" in creator
            ? grant.serviceAccountId === creator.serviceAccountId
            : grant.userId === creator.userId;
        if (!toCreator) {
          throw userFacingError(
            "You don’t have permission to share connectors with others"
          );
        }
        assertRoleDoesNotGrant(role, "connected_app:share");
      }
    }
    await insertGrantsDeduped(tx, grants, row.id, ctx.tenantId);
  }

  if (ctx.recordAuditMetadata) {
    const certificateMetadata = await certificateAuditMetadata(
      {},
      row.configuration
    );
    if (Object.keys(certificateMetadata).length > 0) {
      ctx.recordAuditMetadata(certificateMetadata);
    }
  }
}
